<template>
  <div class="ak-layout-builder" :class="layoutClasses">
    <div class="ak-layout-builder__left">
      <div
        v-if="showDragHandle"
        data-draggable-handle
        class="ak-layout-builder__move"
      >
        <ak-icon type="drag" size="big" />
      </div>
    </div>
    <div
      class="ak-layout-builder__right"
      @mouseover="mouseover"
      @mouseleave="mouseleave"
    >
      <div class="ak-layout-builder__actions" :class="staticIcons">
        <slot name="actions" />
      </div>
      <transition name="fade-right-fast">
        <div v-show="showExpanded" class="ak-layout-builder__expanded">
          <slot name="actionsExpanded" />
        </div>
      </transition>
    </div>
    <slot />
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-layout-builder>...</ak-layout-builder>
   */
  name: 'AkLayoutBuilder',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Show the drag handle or not
     */
    showDrag: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {String}
     * @description Show the drag handle or not
     */
    noShadow: {
      type: Boolean,
      default: false,
    },
    staticIcon: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const showDragHandle = ref(props.showDrag)
    const showExpanded = ref(false)

    const mouseover = () => {
      showExpanded.value = true
    }

    const mouseleave = () => {
      showExpanded.value = false
    }

    const layoutClasses = computed(() => {
      return {
        'ak-layout-builder--no-shadow': props.noShadow,
      }
    })
    const staticIcons = computed(() => {
      return {
        'ak-layout-builder__actions--static': props.staticIcon,
      }
    })

    return {
      showDragHandle,
      showExpanded,
      mouseover,
      mouseleave,
      layoutClasses,
      staticIcons,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-layout-builder {
  $self: &;
  position: relative;
  flex-shrink: 0;
  transition: $transition;

  &:hover {
    box-shadow: $base-shadow-large;
    z-index: 10;
  }
  &--no-shadow {
    &:hover {
      box-shadow: none;
    }
  }
  &__left {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    overflow: hidden;
    height: 100%;
    display: flex;
    align-items: center;
  }

  &__move {
    display: flex;
    align-items: center;
    max-height: 8rem;
    height: 90%;
    border-radius: 0 $base-border-radius $base-border-radius 0;
    background-color: rgba($color-grey-dark, 0.35);
    backdrop-filter: blur(5px);
    color: #fff;
    padding: 0 $spacing-tiny;
    opacity: 0;
    transform: translateX(-1rem);
    transition: $transition;
    &:hover {
      background-color: $color-grey-dark;
      padding: 0 $spacing-small;
    }

    #{$self}:hover & {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &__right {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: row-reverse;
    &:hover {
      z-index: 20;
    }
  }

  &__actions {
    padding-top: $spacing-small;
    padding-right: $spacing-small;
    opacity: 0;
    transform: translateX(1rem);
    transition: $transition;
    &--static {
      transform: translateX(0);
      opacity: 1;
    }
    #{$self}:hover & {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &__expanded {
    padding-top: $spacing-small;
    padding-right: $spacing-tiny;
    display: flex;
  }
}
</style>
